import {
  ButtonPrimeReact,
  Cropper,
  CropperHandle,
} from "@finanedi.es/storybook"
import Spinner from "common/components/Spinner"
import { useCallback, useEffect, useRef, useState } from "react"
import { useTranslation } from "react-i18next"

const MobileCamera = ({
  onClose,
  onConfirm,
}: {
  onClose: () => void
  onConfirm: (string: string) => void
}) => {
  const { t } = useTranslation()
  const [hasImage, setHasImage] = useState<boolean>()
  const fileInputRef = useRef<HTMLInputElement>(null)
  const cropperRef = useRef<CropperHandle>(null)

  useEffect(() => {
    if (fileInputRef.current) {
      fileInputRef.current.click()
    }
  }, [])

  const handleCapture = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    if (file) {
      cropperRef.current?.handleFileChange(file)
    }
  }

  const resetCropper = useCallback(() => {
    onClose()
    cropperRef.current?.resetCropper()
  }, [onClose])

  const handleConfirm = () => {
    cropperRef.current?.getBlob().then((blob) => {
      if (blob) {
        const imageUrl = URL.createObjectURL(blob)
        onConfirm(imageUrl)
      } else {
        // eslint-disable-next-line no-console
        console.error("Blob not available")
      }
    })
  }

  useEffect(() => {
    const checkImageStatus = () => {
      const currentHasImage = cropperRef.current?.hasImage()
      if (currentHasImage !== hasImage) {
        setHasImage(currentHasImage)
      }
    }
    checkImageStatus()
  }, [hasImage])

  useEffect(() => {
    const input = document.getElementById("mobile-camera-input")
    input?.addEventListener("cancel", resetCropper)

    return () => {
      input?.removeEventListener("cancel", resetCropper)
    }
  }, [resetCropper])

  return (
    <div
      className='tw-absolute tw-top-0 tw-left-0 tw-w-full tw-h-full data-[has-image=true]:tw-bg-white tw-pt-16 tw-pb-10 tw-z-10'
      data-has-image={Boolean(hasImage)}
    >
      <div
        className='tw-bg-white tw-h-lvh tw-w-lvw tw-flex tw-justify-center tw-items-center data-[has-image=true]:tw-hidden'
        data-has-image={Boolean(hasImage)}
      >
        <Spinner />
      </div>
      <div className='tw-relative tw-h-[calc(100dvh-124px)] tw-w-full'>
        <Cropper
          ref={cropperRef}
          onImageChange={setHasImage}
        />
      </div>
      <div
        className='tw-hidden data-[has-image=true]:tw-flex tw-justify-between tw-items-center tw-px-2 tw-pt-1'
        data-has-image={Boolean(hasImage)}
      >
        <ButtonPrimeReact
          type='button'
          label={`${t("bancos.cardsTable.deleteModal.cancelButton")}`}
          className='tw-h-12 !tw-bg-inherit !tw-text-primary !tw-border-none tw-items-center'
          onClick={resetCropper}
        />
        <ButtonPrimeReact
          type='button'
          label={`${t("bancos.cardsTable.deleteModal.confirmButton")}`}
          className='tw-h-12 !tw-bg-primary tw-text-simple-10 tw-items-center'
          onClick={handleConfirm}
        />
      </div>
      <input
        id='mobile-camera-input'
        ref={fileInputRef}
        type='file'
        accept='image/*'
        capture='environment'
        onChange={handleCapture}
        className='tw-hidden'
      />
    </div>
  )
}

export default MobileCamera
